/**
 * Component to manage the company-wide list of boxes usable by any carrier
 *
 * @component
 */

import React, { Component } from 'react';
import { Row, Col, Input, Select, Button, Popconfirm, Table, Modal, message } from 'antd';
import axios from 'axios';
import { WEIGHT_UNIT, LENGTH_UNIT } from '../../../../constants/constants';

const { Option } = Select;

const EMPTY_BOX = {
  id: null,
  name: '',
  length: '',
  width: '',
  height: '',
  tareWeight: '',
  dimensionsUnit: 'in',
  weightUnit: 'lb',
  matchOrderTags: [],
  matchProductTags: [],
};

class Boxes extends Component {
  constructor(props) {
    super(props);
    this.state = {
      loading: false,
      boxes: [],
      editingBox: null,
    };
  }

  componentDidMount() {
    this.getBoxes();
  }

  getBoxes = async () => {
    try {
      this.setState({ loading: true });
      const response = await axios.get('/api/boxSettings');
      this.setState({ loading: false, boxes: response.data.boxes || [] });
    } catch (error) {
      this.setState({ loading: false });
      message.error('Error getting boxes');
    }
  };

  startAdd = () => {
    this.setState({ editingBox: { ...EMPTY_BOX } });
  };

  startEdit = (box) => {
    this.setState({ editingBox: { ...box } });
  };

  cancelEdit = () => {
    this.setState({ editingBox: null });
  };

  updateEditingField = (field, value) => {
    this.setState({ editingBox: { ...this.state.editingBox, [field]: value } });
  };

  updateEditingTagList = (field, rawText) => {
    const tags = rawText.split(',').map((tag) => tag.trim()).filter((tag) => tag.length > 0);
    this.updateEditingField(field, tags);
  };

  saveEditingBox = async () => {
    try {
      const response = await axios.post('/api/boxSettings/update', this.state.editingBox);
      if (response.data?.message) {
        return message.error(response.data.message, 5);
      }
      message.success('Box saved.');
      this.setState({ editingBox: null });
      this.getBoxes();
    } catch (err) {
      const errorMessage = err?.response?.data?.message;
      message.error(errorMessage || 'Error saving box. Try again.', 5);
    }
  };

  deleteBox = async (id) => {
    try {
      const response = await axios.post('/api/boxSettings/delete', { id });
      if (response.data?.message) {
        return message.error(response.data.message, 6);
      }
      message.success('Box deleted.');
      this.getBoxes();
    } catch (err) {
      const errorMessage = err?.response?.data?.message;
      message.error(errorMessage || 'Error deleting box. Try again.', 6);
    }
  };

  render() {
    const columns = [
      { title: 'Name', dataIndex: 'name', key: 'name' },
      { title: 'Length', dataIndex: 'length', key: 'length' },
      { title: 'Width', dataIndex: 'width', key: 'width' },
      { title: 'Height', dataIndex: 'height', key: 'height' },
      {
        title: 'Dimension unit',
        dataIndex: 'dimensionsUnit',
        key: 'dimensionsUnit',
        render: (dimensionsUnit) => <span>{LENGTH_UNIT[dimensionsUnit]}</span>,
      },
      { title: 'Empty box weight', dataIndex: 'tareWeight', key: 'tareWeight' },
      {
        title: 'Weight unit',
        dataIndex: 'weightUnit',
        key: 'weightUnit',
        render: (weightUnit) => <span>{WEIGHT_UNIT[weightUnit]}</span>,
      },
      {
        title: 'Edit',
        key: 'edit',
        render: (_, box) => <Button size="small" onClick={() => this.startEdit(box)}>Edit</Button>,
      },
      {
        title: 'Delete',
        key: 'delete',
        render: (_, box) => (
          <Popconfirm title="Are you sure you want to delete this box?" onConfirm={() => this.deleteBox(box.id)} okText="Yes" cancelText="No">
            <Button size="small" danger>Delete</Button>
          </Popconfirm>
        ),
      },
    ];

    return (
      <React.Fragment>
        <Row>
          <Col span={24}>
            <h4>Boxes</h4>
            <span>
              Add the boxes you use for returns, with their dimensions and
              empty weight. Boxes defined here can be used across any
              carrier. Dimension unit (centimeter/inch) and weight unit
              (gram/pound) can be set independently for each box. Use either
              a full number (e.g. 30) or a point for a decimal (e.g. 10.50).
              Do NOT use a comma. Box names must be unique.
            </span>
          </Col>
        </Row>
        <Row style={{ marginTop: '2%' }}>
          <Col span={24}>
            <Table
              style={{ width: '100%' }}
              columns={columns}
              dataSource={this.state.boxes}
              rowKey="id"
              pagination={false}
              bordered
              scroll={{ x: 'max-content' }}
              loading={this.state.loading}
            />
          </Col>
        </Row>
        <Row style={{ marginTop: '2%' }}>
          <Button onClick={this.startAdd}>Add box</Button>
        </Row>
        {this.state.editingBox && (
          <Modal
            title={this.state.editingBox.id ? 'Edit box' : 'Add box'}
            visible={true}
            onCancel={this.cancelEdit}
            onOk={this.saveEditingBox}
            okText="Save"
            cancelText="Cancel"
          >
            <Input addonBefore="Name" value={this.state.editingBox.name} onChange={(e) => this.updateEditingField('name', e.target.value)} />
            <Input style={{ marginTop: '2%' }} addonBefore="Length" value={this.state.editingBox.length} onChange={(e) => this.updateEditingField('length', e.target.value)} />
            <Input style={{ marginTop: '2%' }} addonBefore="Width" value={this.state.editingBox.width} onChange={(e) => this.updateEditingField('width', e.target.value)} />
            <Input style={{ marginTop: '2%' }} addonBefore="Height" value={this.state.editingBox.height} onChange={(e) => this.updateEditingField('height', e.target.value)} />
            <Select
              style={{ width: '100%', marginTop: '2%' }}
              value={this.state.editingBox.dimensionsUnit}
              onChange={(value) => this.updateEditingField('dimensionsUnit', value)}
            >
              <Option value="cm">Centimeter</Option>
              <Option value="in">Inch</Option>
            </Select>
            <Input style={{ marginTop: '2%' }} addonBefore="Empty box weight" value={this.state.editingBox.tareWeight} onChange={(e) => this.updateEditingField('tareWeight', e.target.value)} />
            <Select
              style={{ width: '100%', marginTop: '2%' }}
              value={this.state.editingBox.weightUnit}
              onChange={(value) => this.updateEditingField('weightUnit', value)}
            >
              <Option value="lb">Pound</Option>
              <Option value="g">Gram</Option>
            </Select>
            <Input
              style={{ marginTop: '2%' }}
              addonBefore="Match order tags"
              placeholder="e.g. vip, fragile"
              value={(this.state.editingBox.matchOrderTags || []).join(', ')}
              onChange={(e) => this.updateEditingTagList('matchOrderTags', e.target.value)}
            />
            <span style={{ display: 'block', marginTop: 4, color: 'rgba(0, 0, 0, 0.45)', fontSize: 12 }}>
              If the order being returned has any of these tags, this box will
              be used automatically (applies regardless of how many items are
              in the return; if a matching product tag below also applies to
              a single-item return, the product tag wins).
            </span>
            <Input
              style={{ marginTop: '2%' }}
              addonBefore="Match product tags"
              placeholder="e.g. boots, winter-boots"
              value={(this.state.editingBox.matchProductTags || []).join(', ')}
              onChange={(e) => this.updateEditingTagList('matchProductTags', e.target.value)}
            />
            <span style={{ display: 'block', marginTop: 4, color: 'rgba(0, 0, 0, 0.45)', fontSize: 12 }}>
              If the product being returned has any of these tags, this box
              will be used automatically (only applies to single-item
              returns).
            </span>
          </Modal>
        )}
      </React.Fragment>
    );
  }
}

export default Boxes;
